'use client'

import { useRef } from 'react'
import dynamic from 'next/dynamic'
import { motion, useInView } from 'framer-motion'

const ParticleField = dynamic(() => import('@/components/three/ParticleField'), {
  ssr: false,
  loading: () => <div className="w-full h-full bg-[#0F172A]" />,
})

const stats = [
  { value: '15+', label: 'Projects Delivered' },
  { value: '5+', label: 'Years Experience' },
  { value: '3', label: 'Service Domains' },
  { value: '100%', label: 'Client Satisfaction' },
]

function StatItem({ stat, index }: { stat: typeof stats[0]; index: number }) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.12 }}
      className="flex flex-col gap-2 border-l border-[#C8A96E]/20 pl-6"
    >
      <span
        className="text-5xl md:text-6xl text-[#C8A96E] leading-none"
        style={{ fontFamily: 'var(--font-bebas, "Bebas Neue", cursive)', letterSpacing: '0.02em' }}
      >
        {stat.value}
      </span>
      <span
        className="text-[#94A3B8] text-xs tracking-[0.2em]"
        style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
      >
        {stat.label.toUpperCase()}
      </span>
    </motion.div>
  )
}

export default function WhyApex() {
  return (
    <section className="relative py-32 bg-[#0F172A] overflow-hidden">
      {/* Radial gradient bg */}
      <div className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(13,13,40,0.8) 0%, #0F172A 70%)',
        }}
      />

      {/* Three.js particle bg */}
      <div className="absolute inset-0 opacity-40">
        <ParticleField />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Left: Big italic text */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-px bg-[#C8A96E]" />
              <span
                className="text-[#C8A96E] text-xs tracking-[0.3em]"
                style={{ fontFamily: 'var(--font-space-mono, "Space Mono", monospace)' }}
              >
                WHY APEX
              </span>
            </div>
            <blockquote
              className="text-[clamp(1.8rem,4vw,3.5rem)] leading-[1.15] text-[#F8FAFC] italic"
              style={{ fontFamily: 'var(--font-dm-sans, "DM Sans", sans-serif)', fontWeight: 300 }}
            >
              "We don't build websites. We build{' '}
              <span className="text-[#C8A96E] not-italic font-semibold">digital experiences</span>
              ."
            </blockquote>

            <p className="mt-8 text-[#94A3B8] leading-relaxed max-w-md">
              At Apex Visualtech, every project begins with a deep understanding of your goals. We combine
              technical precision with creative vision to deliver work that doesn't just look good — it
              performs, converts, and endures.
            </p>
          </motion.div>

          {/* Right: Stats grid */}
          <div>
            <div className="grid grid-cols-2 gap-8 mb-12">
              {stats.map((stat, index) => (
                <StatItem key={stat.label} stat={stat} index={index} />
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-[#94A3B8] text-sm leading-relaxed border-t border-white/10 pt-8"
            >
              We operate across three service domains — IT Engineering, Videography, and Graphic Design —
              enabling unified creative strategy and seamless execution for our partners.
            </motion.p>
          </div>
        </div>
      </div>
    </section>
  )
}
